import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PeriodKey, PeriodSel } from './homeApi';
import { addDaysYmd, athensYmd, daysBetween, fmtDM, isYmd } from './format';

/**
 * Σήμερα · Χθες · 7 ημ. · 30 ημ. · Προσαρμογή… (δύο ημερομηνίες). Το «Σήμερα» ξαναφορτώνει όταν είναι ήδη
 * επιλεγμένο — και μετρά τα πατήματα για τις κρυφές «Εκπαιδευτικές» (onTodayTap).
 */
export interface PeriodPickerProps {
  sel: PeriodSel;
  onChange: (sel: PeriodSel) => void;
  onTodayTap?: () => void;
}

const KEYS: PeriodKey[] = ['today', 'yesterday', '7d', '30d', 'custom'];
/** Ίδιο όριο με τον server (MAX_CUSTOM_DAYS). */
export const MAX_RANGE_DAYS = 366;

export const PeriodPicker: React.FC<PeriodPickerProps> = ({ sel, onChange, onTodayTap }) => {
  const { t } = useTranslation(['home']);
  const uid = useId();
  const [open, setOpen] = useState(false);
  const today = athensYmd(new Date());
  const [from, setFrom] = useState(sel.from || addDaysYmd(today, -6));
  const [to, setTo] = useState(sel.to || today);
  const boxRef = useRef<HTMLDivElement>(null);
  const customRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        customRef.current?.focus();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const label = (key: PeriodKey): string => {
    switch (key) {
      case 'today': return t('home:period.today');
      case 'yesterday': return t('home:period.yesterday');
      case '7d': return t('home:period.d7');
      case '30d': return t('home:period.d30');
      default:
        return sel.key === 'custom' && isYmd(sel.from) && isYmd(sel.to)
          ? (sel.from === sel.to ? fmtDM(sel.from) : `${fmtDM(sel.from)} – ${fmtDM(sel.to)}`)
          : t('home:period.custom');
    }
  };

  const pick = (key: PeriodKey) => {
    if (key === 'custom') {
      if (sel.key === 'custom') {
        setFrom(sel.from || from);
        setTo(sel.to || to);
      }
      setOpen(o => !o);
      return;
    }
    setOpen(false);
    if (key === 'today') onTodayTap?.();
    onChange({ key });
  };

  const valid = isYmd(from) && isYmd(to) && from <= to && to <= today && daysBetween(from, to) + 1 <= MAX_RANGE_DAYS;
  const apply = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setOpen(false);
    onChange({ key: 'custom', from, to });
  };

  return (
    <div className="xh-period" ref={boxRef}>
      <div className="xh-seg" role="radiogroup" aria-label={t('home:period.label')}>
        {KEYS.map(key => (
          <button
            key={key}
            ref={key === 'custom' ? customRef : undefined}
            type="button"
            role="radio"
            aria-checked={sel.key === key}
            aria-expanded={key === 'custom' ? open : undefined}
            aria-controls={key === 'custom' ? `${uid}-custom` : undefined}
            onClick={() => pick(key)}
            data-testid={`period-${key}`}
          >
            {label(key)}
          </button>
        ))}
      </div>
      {open && (
        <form id={`${uid}-custom`} className="xh-pop" role="dialog" aria-label={t('home:period.customTitle')} onSubmit={apply}>
          <div className="xh-pop-row">
            <label className="xh-pop-field">
              <span>{t('home:period.from')}</span>
              <input type="date" value={from} max={to || today} onChange={e => setFrom(e.target.value)} data-testid="period-from" />
            </label>
            <label className="xh-pop-field">
              <span>{t('home:period.to')}</span>
              <input type="date" value={to} min={from} max={today} onChange={e => setTo(e.target.value)} data-testid="period-to" />
            </label>
          </div>
          {!valid && <p className="xh-pop-error" role="alert">{t('home:period.invalidRange')}</p>}
          <div className="xh-pop-actions">
            <button type="button" className="xh-btn" onClick={() => setOpen(false)}>{t('home:period.cancel')}</button>
            <button type="submit" className="xh-btn xh-btn-primary" disabled={!valid}>{t('home:period.apply')}</button>
          </div>
        </form>
      )}
    </div>
  );
};

export default PeriodPicker;
